// admin/pages/Categories.js - UPDATED with Vendor Settings sync

import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Image,
  Spinner,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import { motion } from "framer-motion";
import { FaTrash, FaEdit, FaSync, FaCheck, FaUpload, FaImage, FaSyncAlt } from "react-icons/fa";
import axios from "axios";

import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:7000";
const API_URL = `${API_BASE}/admin/companies`;

const Categories = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [newCompany, setNewCompany] = useState({
    name: "",
    status: "Active",
    description: "",
    logo: null,
  });

  const [editCompany, setEditCompany] = useState({
    _id: "",
    name: "",
    status: "Active",
    description: "",
    logo: null,
    currentLogo: "",
  });

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [syncingLogo, setSyncingLogo] = useState(false);

  // ✅ FIXED: Image URL normalization - Same as User Side
// ✅ FIXED: IMAGE NORMALIZATION FUNCTION
const normalizeImage = (image) => {
  if (!image) return null;

  // Already full URL
  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  // ✅ Admin uploaded image (starts with /images) → Port 7001
  if (image.startsWith("/images")) {
    return `https://api-admin.native91.com${image}`;
  }

  // ✅ Vendor uploaded image (starts with /uploads) → Port 5177
  if (image.startsWith("/uploads")) {
    return `https://api-vendor.native91.com${image}`;
  }

  // Just filename
  if (!image.includes("/") && !image.includes("\\")) {
    return `https://api-vendor.native91.com/uploads/logos/${image}`;
  }

  // Relative path
  return `https://api-vendor.native91.com/${image}`;
};

  const fetchCompanies = useCallback(async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      const { data } = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCompanies(data.companies || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }

    setLoading(false);
  }, [API_URL]);

  // Initial fetch
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Poll for updates every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCompanies();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchCompanies]);

  // ---------------- DELETE COMPANY ----------------
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this company?"))
      return;

    try {
      const token = localStorage.getItem("token");

      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCompanies(companies.filter((c) => c._id !== id));
      setSuccessMessage("Company deactivated successfully!");
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to deactivate company");
    }
  };

  // ---------------- SYNC LOGO ----------------
  const handleSyncLogo = async (companyId) => {
    if (!window.confirm("Sync logo from vendor settings?")) return;

    try {
      setSyncingLogo(true);
      const token = localStorage.getItem("token");

      const { data } = await axios.post(
        `${API_URL}/${companyId}/sync-logo`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        setSuccessMessage("✅ Logo synced successfully from vendor settings!");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
        await fetchCompanies();
      }
    } catch (err) {
      console.error("Error syncing logo:", err);
      alert(err.response?.data?.message || "Failed to sync logo");
    } finally {
      setSyncingLogo(false);
    }
  };

  // ---------------- MODAL HANDLERS ----------------
  const handleShow = () => {
    setNewCompany({
      name: "",
      status: "Active",
      description: "",
      logo: null,
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setShowEditModal(false);
    setNewCompany({
      name: "",
      status: "Active",
      description: "",
      logo: null,
    });
    setEditCompany({
      _id: "",
      name: "",
      status: "Active",
      description: "",
      logo: null,
      currentLogo: "",
    });
  };

  // ---------------- EDIT HANDLERS ----------------
  const handleEditShow = (company) => {
    setEditCompany({
      _id: company._id,
      name: company.name,
      status: company.status || "Active",
      description: company.description || "",
      logo: null,
      currentLogo: company.logo || "",
    });
    setShowEditModal(true);
  };

  const handleFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      e.target.value = "";
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPEG, PNG, GIF, WebP, and SVG files are allowed");
      e.target.value = "";
      return;
    }

    if (isEdit) {
      setEditCompany({
        ...editCompany,
        logo: file,
      });
    } else {
      setNewCompany({
        ...newCompany,
        logo: file,
      });
    }
  };

  // ---------------- ADD COMPANY ----------------
  const handleAddCompany = async () => {
    if (!newCompany.name) {
      return alert("Name is required");
    }

    setAdding(true);

    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("name", newCompany.name);
      formData.append("status", newCompany.status);
      formData.append("description", newCompany.description || "");

      if (newCompany.logo) {
        formData.append("logo", newCompany.logo);
      }

      const { data } = await axios.post(API_URL, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setCompanies([...companies, data.company]);
      setSuccessMessage("Company added successfully!");
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
      handleClose();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to add company");
    }

    setAdding(false);
  };

  // ---------------- EDIT COMPANY ----------------
  const handleEditCompany = async () => {
    if (!editCompany.name) {
      return alert("Name is required");
    }

    setEditing(true);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("name", editCompany.name);
      formData.append("status", editCompany.status);
      formData.append("description", editCompany.description || "");

      if (editCompany.logo) {
        formData.append("logo", editCompany.logo);
      }

      const { data } = await axios.put(`${API_URL}/${editCompany._id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setCompanies(companies.map(c => c._id === data.company._id ? data.company : c));
      
      setSuccessMessage("✅ Company updated successfully! Logo synced everywhere.");
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
      
      handleClose();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to update company");
    }

    setEditing(false);
  };

  // ---------------- MANUAL SYNC ----------------
  const handleSync = async () => {
    setSyncing(true);
    await fetchCompanies();
    setSyncing(false);
    setSuccessMessage("✅ Companies synced successfully!");
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  // ---------------- LOADING ----------------
  if (loading) {
    return (
      <div>
        <Header />
        <Sidebar />
        <div className="text-center py-5">
          <Spinner animation="border" /> Loading companies...
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Sidebar />

      <div className="admin-layout">
        <main className="admin-content">
          <Container fluid>
            {/* Success Alert */}
            {showSuccessAlert && (
              <Alert variant="success" dismissible onClose={() => setShowSuccessAlert(false)}>
                <FaCheck className="me-2" />
                {successMessage}
              </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <motion.h4
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="d-inline-block"
                >
                  Companies
                </motion.h4>
                <Badge bg="secondary" className="ms-2">
                  {companies.length}
                </Badge>
                {lastUpdated && (
                  <span className="text-muted ms-3" style={{ fontSize: "12px" }}>
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </div>

              <div>
                <Button 
                  variant="outline-secondary" 
                  className="me-2"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <FaSync className={syncing ? "fa-spin" : ""} /> 
                  {syncing ? "Syncing..." : "Sync"}
                </Button>
                <Button onClick={handleShow}>
                  Add Company
                </Button>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="table-wrapper"
            >
              <Table responsive bordered hover>
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Description</th>
                    <th>Logo</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {companies.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No companies found.
                      </td>
                    </tr>
                  ) : (
                    companies.map((company, index) => {
                      const imageUrl = normalizeImage(company.logo);
                      const hasLogo = imageUrl && company.logo;

                      return (
                        <motion.tr
                          key={company._id || index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td>
                            <strong>{company.name}</strong>
                          </td>
                          <td style={{ maxWidth: "200px" }}>
                            <div style={{ 
                              maxHeight: "60px", 
                              overflow: "hidden", 
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {company.description || "No description"}
                            </div>
                          </td>
                          <td>
                            {hasLogo ? (
                              <Image
                                src={imageUrl}
                                rounded
                                style={{
                                  maxHeight: "80px",
                                  maxWidth: "80px",
                                  objectFit: "contain",
                                  border: "1px solid #eee",
                                  padding: "4px",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = "/images/default-logo.png";
                                }}
                              />
                            ) : (
                              <div style={{
                                width: "80px",
                                height: "80px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px dashed #dee2e6",
                                flexDirection: "column",
                                padding: "8px"
                              }}>
                                <FaImage size={24} className="text-muted" />
                                <small className="text-muted" style={{ fontSize: "10px" }}>
                                  No Logo
                                </small>
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge
                              bg={
                                company.status === "Active"
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {company.status || "Active"}
                            </Badge>
                          </td>

                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-1"
                              onClick={() => handleEditShow(company)}
                              title="Edit Company"
                            >
                              <FaEdit />
                            </Button>
                            {company.logo && (
                              <Button
                                size="sm"
                                variant="outline-info"
                                className="me-1"
                                onClick={() => handleSyncLogo(company._id)}
                                disabled={syncingLogo}
                                title="Sync logo from vendor settings"
                              >
                                <FaSyncAlt className={syncingLogo ? "fa-spin" : ""} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(company._id)}
                              title="Deactivate Company"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </motion.div>

            {/* ---------------- ADD COMPANY MODAL ---------------- */}
            <Modal show={showModal} onHide={handleClose}>
              <Modal.Header closeButton>
                <Modal.Title>Add Company</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={newCompany.name}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter company name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={newCompany.description}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter company description"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={newCompany.status}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Logo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, false)}
                    />
                    <Form.Text className="text-muted">
                      JPEG, PNG, GIF, WebP, SVG (Max 5MB)
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddCompany} disabled={adding}>
                  {adding ? "Adding..." : "Add Company"}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* ---------------- EDIT COMPANY MODAL ---------------- */}
            <Modal show={showEditModal} onHide={handleClose} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Edit Company</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <Form>
                  {editCompany.currentLogo && (
                    <div className="mb-3 text-center">
                      <Form.Label>Current Logo</Form.Label>
                      <div>
                        <Image
                          src={normalizeImage(editCompany.currentLogo)}
                          rounded
                          style={{
                            maxHeight: "100px",
                            maxWidth: "100px",
                            objectFit: "contain",
                            border: "1px solid #eee",
                            padding: "4px",
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/images/default-logo.png";
                          }}
                        />
                      </div>
                      <small className="text-muted">Upload a new logo to replace this one</small>
                    </div>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Company Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={editCompany.name}
                      onChange={(e) =>
                        setEditCompany({
                          ...editCompany,
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter company name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editCompany.description}
                      onChange={(e) =>
                        setEditCompany({
                          ...editCompany,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter company description"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editCompany.status}
                      onChange={(e) =>
                        setEditCompany({
                          ...editCompany,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Logo (optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, true)}
                    />
                    <Form.Text className="text-muted">
                      Leave empty to keep existing logo. JPEG, PNG, GIF, WebP, SVG (Max 5MB)
                    </Form.Text>
                    {editCompany.logo && (
                      <div className="mt-2">
                        <small className="text-success">
                          <FaUpload className="me-1" />
                          New logo selected: {editCompany.logo.name}
                        </small>
                      </div>
                    )}
                  </Form.Group>

                  <Alert variant="info" className="mt-3">
                    <strong>ℹ️ Note:</strong> Updating the logo here will sync it to:
                    <ul className="mb-0 mt-1">
                      <li>Vendor Settings</li>
                      <li>Vendor Documents</li>
                      <li>User Side (Product Page)</li>
                    </ul>
                  </Alert>
                </Form>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleEditCompany} disabled={editing}>
                  {editing ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Updating...
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" /> Update Company
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default Categories;
